import React, { useState, useEffect } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import DatePicker from '@/components/ui/DatePicker';
import Select from '@/components/ui/Select';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const ShiftModal = ({ 
  isOpen, 
  onClose, 
  shift, 
  employees, 
  onSave, 
  onDelete,
  mode = 'edit' // 'create', 'edit', 'view'
}) => {
  const { t } = useTranslation(['employeeScheduling']);
  const [formData, setFormData] = useState({
    employeeId: '',
    date: '',
    startTime: '',
    endTime: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (shift && mode !== 'create') {
      setFormData({
        employeeId: shift?.employeeId || '',
        date: shift?.date || '',
        startTime: shift?.startTime || '',
        endTime: shift?.endTime || '',
        notes: shift?.notes || '',
      });
    } else if (mode === 'create') {
      setFormData({
        employeeId: '',
        date: new Date()?.toISOString()?.split('T')?.[0],
        startTime: '09:00',
        endTime: '17:00',
        notes: '',
      });
    }
  }, [shift, mode, isOpen]);

  const employeeOptions = employees?.map((emp) => ({
    value: emp?.id,
    label: emp?.name,
    description: `${emp?.role ?? ''} • ${emp?.email ?? ''}`,
  }));

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.employeeId) {
      newErrors.employeeId = t('employeeScheduling.modal.validation.employee');
    }
    if (!formData?.date) {
      newErrors.date = t('employeeScheduling.modal.validation.date');
    }
    if (!formData?.startTime) {
      newErrors.startTime = t('employeeScheduling.modal.validation.startTime');
    }
    if (!formData?.endTime) {
      newErrors.endTime = t('employeeScheduling.modal.validation.endTime');
    }
    if (formData?.startTime && formData?.endTime && formData?.startTime >= formData?.endTime) {
      newErrors.endTime = t('employeeScheduling.modal.validation.endTimeOrder');
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const calculateShiftDuration = () => {
    if (!formData?.startTime || !formData?.endTime) return 0;

    const start = new Date(`2000-01-01T${formData.startTime}`);
    const end = new Date(`2000-01-01T${formData.endTime}`);
    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);

    return Math.max(0, diffHours);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const cleanedNotes = formData?.notes?.trim?.() ?? '';
      const shiftData = {
        employeeId: formData?.employeeId,
        date: formData?.date,
        startTime: formData?.startTime,
        endTime: formData?.endTime,
        notes: cleanedNotes,
        role: resolvedRole,
        employeeName: selectedEmployee?.name ?? shift?.employeeName ?? '',
        duration: calculateShiftDuration(),
        status: shift?.status ?? 'scheduled',
      };

      if (shift?.id) {
        shiftData.id = shift.id;
      }

      await onSave(shiftData);
      onClose();
    } catch (error) {
      console.error('Error saving shift:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(t('employeeScheduling.modal.actions.confirmDelete'))) {
      setIsLoading(true);
      try {
        await onDelete(shift?.id);
        onClose();
      } catch (error) {
        console.error('Error deleting shift:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const selectedEmployee = employees?.find(emp => emp?.id === formData?.employeeId);
  const resolvedRole = selectedEmployee?.role ?? shift?.role ?? '';
  const normalizedRoleKey = resolvedRole?.toString?.().toLowerCase?.();
  const roleDisplay = resolvedRole
    ? t(`employeeScheduling.roles.${normalizedRoleKey}`, resolvedRole)
    : '—';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevation-2 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {mode === 'create'
                ? t('employeeScheduling.modal.titles.create')
                : mode === 'edit'
                  ? t('employeeScheduling.modal.titles.edit')
                  : t('employeeScheduling.modal.titles.view')}
            </h2>
            {selectedEmployee && (
              <p className="text-sm text-muted-foreground mt-1">
                {selectedEmployee?.name} • {selectedEmployee?.role}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Employee Selection */}
            <div className="md:col-span-2">
              <Select
                label={t('employeeScheduling.modal.fields.employee')}
                options={employeeOptions}
                value={formData?.employeeId}
                onChange={(value) => setFormData(prev => ({ ...prev, employeeId: value }))}
                error={errors?.employeeId}
                required
                searchable
                disabled={mode === 'view'}
              />
              
              {selectedEmployee && (
                <div className="mt-3 p-3 bg-muted/20 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <EmployeeAvatar
                      url={selectedEmployee?.avatarUrl}
                      name={selectedEmployee?.name}
                      alt={`${selectedEmployee?.name ?? 'Employee'} profile photo`}
                      status={selectedEmployee?.status}
                      size="sm"
                      showStatus={false}
                      wrapperClassName="border border-border"
                    />
                    <div>
                      <div className="font-medium text-foreground">{selectedEmployee?.name}</div>
                      <div className="text-sm text-muted-foreground">{selectedEmployee?.email}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatTranslation(t('employeeScheduling.modal.employeeMeta'), {
                          hours: selectedEmployee?.hoursThisWeek ?? 0,
                          status: t(
                            `employeeScheduling.availability.${selectedEmployee?.availability ?? 'available'}`,
                            selectedEmployee?.availability,
                          ),
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Date */}
            <DatePicker
              label={t('employeeScheduling.modal.fields.date')}
              value={formData?.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e?.target?.value }))}
              error={errors?.date}
              required
              disabled={mode === 'view'}
            />

            {/* Start Time */}
            <Input
              label={t('employeeScheduling.modal.fields.startTime')}
              type="time"
              value={formData?.startTime}
              onChange={(e) => setFormData(prev => ({ ...prev, startTime: e?.target?.value }))}
              error={errors?.startTime}
              required
              disabled={mode === 'view'}
            />

            {/* End Time */}
            <Input
              label={t('employeeScheduling.modal.fields.endTime')}
              type="time"
              value={formData?.endTime}
              onChange={(e) => setFormData(prev => ({ ...prev, endTime: e?.target?.value }))}
              error={errors?.endTime}
              required
              disabled={mode === 'view'}
            />

            {/* Notes */}
            <div className="md:col-span-2">
              <Input
                label={t('employeeScheduling.modal.fields.notes')}
                type="text"
                value={formData?.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e?.target?.value }))}
                placeholder={t('employeeScheduling.modal.fields.notesPlaceholder')}
                disabled={mode === 'view'}
              />
            </div>
          </div>

          <div className="mt-6 p-4 bg-muted/20 rounded-lg">
            <h3 className="font-medium text-foreground mb-3">
              {t('employeeScheduling.modal.summary.title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">
                  {t('employeeScheduling.modal.summary.duration')}
                </div>
                <div className="font-medium">
                  {formatTranslation(t('employeeScheduling.modal.summary.durationValue'), {
                    hours: calculateShiftDuration()?.toFixed(1),
                  })}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">
                  {t('employeeScheduling.modal.fields.role')}
                </div>
                <div className="font-medium">{roleDisplay}</div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
            <div>
              {mode === 'edit' && shift && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isLoading}
                  iconName="Trash2"
                  iconSize={16}
                >
                  {t('employeeScheduling.modal.actions.delete')}
                </Button>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                {mode === 'view'
                  ? t('employeeScheduling.modal.actions.close')
                  : t('employeeScheduling.modal.actions.cancel')}
              </Button>
              
              {mode !== 'view' && (
                <Button
                  type="submit"
                  variant="default"
                  loading={isLoading}
                  iconName="Save"
                  iconSize={16}
                >
                  {mode === 'create'
                    ? t('employeeScheduling.modal.actions.create')
                    : t('employeeScheduling.modal.actions.save')}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShiftModal;
